import React, { useState } from 'react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { PageHeader, Button, Card, Modal, FormField, Input, Select, Textarea, statusBadge, formatDate, formatCurrency, FORMAT_LABELS, Badge } from '../ui';
import { Plus, Trophy, MapPin, Calendar, Users } from 'lucide-react';

const FORMATS = [
  { value: 'pool_single', label: 'Pool play → Single elimination' },
  { value: 'single_elimination', label: 'Single elimination' },
  { value: 'double_elimination', label: 'Double elimination' },
  { value: 'round_robin', label: 'Round robin (league)' },
];

export default function Tournaments() {
  const { tournaments, teams, registrations } = useApp();
  const { isAdmin } = useAuth();
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ name: '', date1: '', date2: '', location: '', description: '', ageGroups: [{ group: 'U12', format: 'pool_single', maxTeams: 8, fee: 350 }] });

  const getTeamCount = (t) => t.ageGroups?.reduce((s, ag) => s + (ag.teams?.length || 0), 0) || 0;
  const getMaxTeams = (t) => t.ageGroups?.reduce((s, ag) => s + (ag.maxTeams || 0), 0) || 0;
  const getTeam = id => teams.find(t => t.id === id);

  const addAgeGroup = () => setForm(p => ({ ...p, ageGroups: [...p.ageGroups, { group: 'U10', format: 'single_elimination', maxTeams: 8, fee: 300 }] }));
  const removeAgeGroup = (i) => setForm(p => ({ ...p, ageGroups: p.ageGroups.filter((_, idx) => idx !== i) }));
  const updateAG = (i, key, val) => setForm(p => ({ ...p, ageGroups: p.ageGroups.map((ag, idx) => idx === i ? { ...ag, [key]: val } : ag) }));

  const formatBadge = (fmt) => {
    const colors = {
      pool_single: { bg: 'rgba(30,70,160,0.18)', color: '#80b0f8' },
      single_elimination: { bg: 'rgba(199,163,93,0.16)', color: 'var(--gold-bright)' },
      double_elimination: { bg: 'rgba(160,30,60,0.18)', color: '#f07090' },
      round_robin: { bg: 'rgba(40,120,60,0.18)', color: '#6eca7c' },
    };
    const c = colors[fmt] || { bg: 'var(--surface-1)', color: 'var(--text-secondary)' };
    return <span style={{ ...c, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20 }}>{FORMAT_LABELS[fmt] || fmt}</span>;
  };

  return (
    <div>
      <PageHeader title="Tournaments & Leagues" subtitle={`${tournaments.length} events · manage formats, age groups, and registrations`}
        actions={isAdmin && <Button variant="primary" onClick={() => setShowNew(true)}><Plus size={15} /> New tournament</Button>}
      />

      {/* Tournament cards */}
      {!selected && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          {tournaments.map(t => {
            const regCount = getTeamCount(t);
            const maxCount = getMaxTeams(t);
            const pct = maxCount > 0 ? (regCount / maxCount) * 100 : 0;
            return (
              <div key={t.id} onClick={() => setSelected(t)}
                style={{ background: 'var(--surface-0)', border: '1px solid var(--border)', borderRadius: 14, padding: '1.25rem', cursor: 'pointer', transition: 'all 0.15s' }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold-main)'; e.currentTarget.style.boxShadow = 'var(--shadow-md)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = ''; }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <Trophy size={20} color="var(--brand-gold)" />
                  {statusBadge(t.status)}
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 800, fontFamily: 'var(--font-condensed)', color: 'var(--text-primary)', marginBottom: 4 }}>{t.name}</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 2 }}>
                  <Calendar size={13} /> {t.dates?.join(' – ')}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 12 }}>
                  <MapPin size={13} /> {t.location}
                </div>
                <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 12 }}>
                  {t.ageGroups?.map(ag => (
                    <span key={ag.group} style={{ fontSize: 12, fontWeight: 700, background: 'var(--surface-2)', color: 'var(--text-secondary)', padding: '2px 8px', borderRadius: 6 }}>{ag.group}</span>
                  ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ flex: 1, height: 4, background: 'var(--surface-2)', borderRadius: 2, overflow: 'hidden' }}>
                    <div style={{ width: `${pct}%`, height: '100%', background: 'var(--gold-main)', borderRadius: 2, transition: 'width 0.3s' }} />
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{regCount}/{maxCount} teams</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tournament detail */}
      {selected && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}>
            <button onClick={() => setSelected(null)} style={{ background: 'none', border: '1px solid var(--border)', padding: '6px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>← Back</button>
            <h2 style={{ fontSize: 20, fontWeight: 800, fontFamily: 'var(--font-condensed)', color: 'var(--text-primary)' }}>{selected.name}</h2>
            {statusBadge(selected.status)}
          </div>

          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.25rem' }}>
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Details</h3>
              {[['Dates', selected.dates?.join(' – ')], ['Location', selected.location], ['Description', selected.description]].map(([k,v]) => (
                <div key={k} style={{ display: 'flex', gap: 12, padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 14 }}>
                  <span style={{ color: 'var(--text-muted)', fontWeight: 600, minWidth: 90 }}>{k}</span>
                  <span style={{ color: 'var(--text-primary)' }}>{v}</span>
                </div>
              ))}
            </Card>
            <Card>
              <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 10 }}>Registration summary</h3>
              {selected.ageGroups?.map(ag => (
                <div key={ag.group} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{ag.group}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>{FORMAT_LABELS[ag.format]}</span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{ag.teams?.length || 0}/{ag.maxTeams}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatCurrency(ag.fee)}</div>
                  </div>
                </div>
              ))}
            </Card>
          </div>

          {/* Age group detail tables */}
          {selected.ageGroups?.map(ag => (
            <Card key={ag.group} padding="0" style={{ marginBottom: '1rem' }}>
              <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontFamily: 'var(--font-condensed)', fontWeight: 800, fontSize: 17 }}>{ag.group}</span>
                {formatBadge(ag.format)}
                <span style={{ color: 'var(--text-muted)', fontSize: 13, marginLeft: 'auto' }}>{ag.teams?.length || 0} / {ag.maxTeams} teams · {formatCurrency(ag.fee)} per team</span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: 'var(--surface-1)' }}>
                    {['Team','City','Players','Registration'].map(h => (
                      <th key={h} style={{ padding: '8px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ag.teams?.length === 0 ? (
                    <tr><td colSpan={4} style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No teams registered for {ag.group} yet</td></tr>
                  ) : ag.teams?.map(tid => {
                    const team = getTeam(tid);
                    return (
                      <tr key={tid} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 28, height: 28, borderRadius: 7, background: team?.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800, color: '#fff' }}>{team?.abbr}</div>
                            <span style={{ fontWeight: 600 }}>{team?.name}</span>
                          </div>
                        </td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>{team?.city}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-secondary)' }}>{team?.playerIds?.length || 0}</td>
                        <td style={{ padding: '10px 14px' }}><Badge variant="green">Confirmed</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Card>
          ))}
        </div>
      )}

      {/* New Tournament Modal */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Create tournament" width={620}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <FormField label="Tournament name" required><Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="Summer Classic 2025" /></FormField>
            <FormField label="Location"><Input value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} placeholder="Park name" /></FormField>
            <FormField label="Start date"><Input type="date" value={form.date1} onChange={e => setForm(p => ({...p, date1: e.target.value}))} /></FormField>
            <FormField label="End date (optional)"><Input type="date" value={form.date2} onChange={e => setForm(p => ({...p, date2: e.target.value}))} /></FormField>
          </div>
          <FormField label="Description">
            <textarea value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} rows={2}
              style={{ width: '100%', padding: '8px 11px', border: '1px solid var(--border)', borderRadius: 8, fontSize: 14, fontFamily: 'var(--font-sans)', resize: 'vertical' }} placeholder="Tournament details…" />
          </FormField>

          {/* Age groups */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <label style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)' }}>Age groups</label>
              <Button variant="secondary" size="sm" onClick={addAgeGroup}><Plus size={12} /> Add group</Button>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {form.ageGroups.map((ag, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '80px 1fr 80px 100px auto', gap: 8, alignItems: 'end', background: 'var(--surface-1)', padding: '10px', borderRadius: 10 }}>
                  <FormField label="Group">
                    <Select value={ag.group} onChange={e => updateAG(i, 'group', e.target.value)}>
                      {['U8','U10','U12','U14','U16','U19'].map(g => <option key={g}>{g}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Format">
                    <Select value={ag.format} onChange={e => updateAG(i, 'format', e.target.value)}>
                      {FORMATS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </Select>
                  </FormField>
                  <FormField label="Max teams">
                    <Input type="number" value={ag.maxTeams} onChange={e => updateAG(i, 'maxTeams', Number(e.target.value))} min={2} />
                  </FormField>
                  <FormField label="Fee ($)">
                    <Input type="number" value={ag.fee} onChange={e => updateAG(i, 'fee', Number(e.target.value))} min={0} />
                  </FormField>
                  {form.ageGroups.length > 1 && (
                    <button onClick={() => removeAgeGroup(i)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#c62828', fontSize: 18, lineHeight: 1, marginBottom: 2 }}>×</button>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', paddingTop: 4 }}>
            <Button variant="secondary" onClick={() => setShowNew(false)}>Cancel</Button>
            <Button variant="primary" onClick={() => setShowNew(false)}>Create tournament</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
